/* ============================================================
   header.js — het gedrag van de kop.

   Drie dingen, en verder niets:
   1. de zoekbalk openen en sluiten (met de overlay eronder),
   2. het mega menu op desktop (hover én toetsenbord — hover alleen is niet
      bedienbaar zonder muis),
   3. de land-/valutakiezer open- en dichtklappen.

   De laden (navigatie, winkelwagen) hebben hun eigen sectie en hun eigen script;
   die worden hier alleen aangeroepen via [data-dialog-trigger].
   ============================================================ */
(() => {
  const header = document.querySelector('.js-header');
  if (!header) return;

  /* ---------- --header-height ----------
     Het origineel zet deze variabele met JavaScript en rekent er op drie plekken
     mee: `scroll-padding-top` (zodat een anker niet onder de kop verdwijnt), de
     hoogte van de laden, en op mobiel de positie van het zoekformulier
     (`top: var(--header-height)`). Zonder deze regel plakt dat formulier over de
     kop heen en vang je elke klik op de burger af — de knop is dan onbruikbaar
     zonder dat je iets ziet. */
  /* Gemeten aan de kop zélf, niet aan de hele header-group: de balk bovenaan
     scrolt mee en telt niet mee in de hoogte waar het origineel mee rekent
     (daar is --header-height 76 px terwijl de groep 116 px hoog is). */
  /* En `--zoekbalk-hoogte`: onder 1024 px staat het zoekformulier bij het
     origineel altijd open, absoluut gepositioneerd op `top: var(--header-height)`.
     Het ligt dus ÓVER de eerste sectie. Bij Iconic valt dat niet op omdat hun
     hero een foto met ingebakken tekst is; bij ons verdwijnt de bovenste regel
     van de kop erachter. Deze maat schuift de pagina er precies onderuit. */
  /* En `--header-group-height`: de hoogte van de héle kop, balk bovenaan
     inbegrepen. De CSS van het origineel rekent er op vier plekken mee (de
     navigatielade, de zoeklaag, de hoogte van het mega menu), en Iconic vult hem
     met JavaScript dat niet is meegekomen. Zonder die maat opende de mobiele
     navigatielade op `top: var(--header-height)` = 76 px — dwars door de
     wordmark heen, want de kop is met de balk erboven 117 px hoog. */
  const meetHoogte = () => {
    const wortel = document.documentElement.style;
    wortel.setProperty('--header-height', `${Math.round(header.getBoundingClientRect().height)}px`);
    const groep = document.querySelector('.js-header-group') || header;
    wortel.setProperty('--header-group-height', `${Math.round(groep.getBoundingClientRect().height)}px`);
    const zoek = header.querySelector('.js-search-form');
    const zoekStijl = zoek && getComputedStyle(zoek);
    const staatOverDePagina = zoek && zoekStijl.position === 'absolute'
      && parseFloat(zoekStijl.opacity) === 1
      && zoekStijl.visibility !== 'hidden';
    wortel.setProperty('--zoekbalk-hoogte',
      staatOverDePagina ? `${Math.round(zoek.getBoundingClientRect().height)}px` : '0px');
  };
  meetHoogte();
  window.addEventListener('resize', meetHoogte);
  if ('ResizeObserver' in window) {
    const kijker = new ResizeObserver(meetHoogte);
    kijker.observe(header);
    const groep = document.querySelector('.js-header-group');
    if (groep && groep !== header) kijker.observe(groep);
  }

  /* ---------- zoeken ---------- */
  const form = header.querySelector('.js-search-form');
  const overlay = header.querySelector('.js-search-overlay');
  const invoer = header.querySelector('.js-search-input');

  const zetZoek = (open) => {
    if (!form) return;
    form.classList.toggle('is-visible', open);
    if (overlay) {
      overlay.classList.toggle('opacity-100', open);
      overlay.classList.toggle('opacity-0', !open);
      overlay.classList.toggle('pointer-events-auto', open);
    }
    header.querySelectorAll('.js-search-trigger').forEach((b) => {
      if (b.tagName === 'BUTTON') b.setAttribute('aria-expanded', String(open));
    });
    if (open && invoer) invoer.focus();
    /* De balk schuift de pagina omlaag zolang hij openstaat; sluit hij, dan hoort
       die ruimte meteen terug. */
    meetHoogte();
  };

  header.querySelectorAll('.js-search-trigger').forEach((knop) => {
    knop.addEventListener('click', (e) => {
      e.preventDefault();
      zetZoek(!form.classList.contains('is-visible'));
    });
  });
  header.querySelectorAll('.js-search-sluiten').forEach((knop) =>
    knop.addEventListener('click', () => zetZoek(false)));
  if (overlay) overlay.addEventListener('click', () => zetZoek(false));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && form && form.classList.contains('is-visible')) zetZoek(false);
  });

  /* ---------- mega menu ---------- */
  /* Het origineel opent op hover. Hover alleen sluit iedereen buiten die geen
     muis gebruikt, dus focus doet hier hetzelfde en Escape sluit. */
  const items = header.querySelectorAll('.nav-desktop__item--has-submenu');
  items.forEach((item) => {
    const link = item.querySelector('.nav-desktop__link');
    const open = (aan) => {
      item.classList.toggle('is-open', aan);
      if (link) link.setAttribute('aria-expanded', String(aan));
    };
    item.addEventListener('pointerenter', () => open(true));
    item.addEventListener('pointerleave', () => open(false));
    item.addEventListener('focusin', () => open(true));
    item.addEventListener('focusout', (e) => {
      if (!item.contains(e.relatedTarget)) open(false);
    });
    item.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { open(false); link?.focus(); }
    });
  });

  /* ---------- land- en valutakiezer ---------- */
  header.querySelectorAll('.disclosure').forEach((kiezer) => {
    const knop = kiezer.querySelector('.disclosure__button');
    const lijst = kiezer.querySelector('.disclosure__list');
    if (!knop || !lijst) return;
    knop.addEventListener('click', () => {
      const open = lijst.hasAttribute('hidden');
      lijst.toggleAttribute('hidden', !open);
      knop.setAttribute('aria-expanded', String(open));
    });
    lijst.querySelectorAll('a[data-value]').forEach((a) =>
      a.addEventListener('click', (e) => {
        e.preventDefault();
        const veld = kiezer.querySelector('input[name="country_code"]');
        if (veld) veld.value = a.dataset.value;
        kiezer.closest('form')?.submit();
      }));
    document.addEventListener('click', (e) => {
      if (!kiezer.contains(e.target)) {
        lijst.setAttribute('hidden', '');
        knop.setAttribute('aria-expanded', 'false');
      }
    });
  });
})();
